import { StockManagement } from "features/iot/stock-management/models/stock-management-models";
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store";
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation";
import stockManagementStore from "features/iot/stock-management/stores/stock-management-store";
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models";
import { Sensor } from "features/iot/sensors/models/sensor-models";

export function groupSensorBatchesStockManagement(): StockManagement[] {
  try {
    const {  sensorBatches } = sensorBatchStore;
    const stockManagementEntries: StockManagement[] = [];
    const partNumbers = new Set(
      sensorBatches.map((s) => s.partNumber?.trim()).filter((p) => p)
    );
    partNumbers.forEach((p) => {
      const onHand = getOnHandSensors(p);
      const onOrder = getOnOrderSensors(p);
      const allocated = getAllocatedSensors(p);
      const dispatched = getDispatched(p);
      const stockEntry: StockManagement = {
        id: p,
        partNumber: p,
        onHand: onHand.length,
        onOrder: onOrder.length,
        allocated: allocated.length,
        available: onHand.length - allocated.length,
        dispatched: dispatched.length,
        ableToCommit: CalculateAbleToCommitSensors(
          onHand.length,
          onOrder.length,
          allocated.length
        ),
      };
      stockManagementEntries.push(stockEntry);
    });

    return stockManagementEntries;
  } catch (e) {
    console.log(
      "Something went wrong while groupSensorBatchesStockManagement:",
      e
    );
    return [];
  }
}

export const isOnHand = (sbSensor:SBSensor,sensor:Sensor |undefined): boolean => {
  //      1- provisioning Id should be NULL ;
  //      2- Manufactured Flag should be TRUE
  //      3- FAULT should be empty.
  //      4- Dispatched state is false
  if (!sbSensor?.details?.provisioningId && sbSensor?.isManufactured &&  !sensor?.fault && !sbSensor?.dispatched ) {
    return true;
  }
  return false;
};
export const isAllocated = (sbSensor:SBSensor,sensor:Sensor |undefined): boolean => {
    // Allocated value is calculated from all sensors that:
  // 1- provisioning Id should be NULL
  // 2- AllocatedState should be true.
  // 3- FAULT should be empty.
  // 4- Dispatched state is false.
  if (!sbSensor?.details?.provisioningId && sbSensor?.allocated &&  !sensor?.fault &&  !sbSensor?.dispatched ) {
    return true;
  }
  return false;
};

export const isOnOrder  = (sbSensor:SBSensor): boolean => {
  //On Order value is calculated from all sensors that have the Manufactured Flag as FALSE.
  if (!sbSensor?.isManufactured) {
    return true;
  }
  return false;
};

export const isDispatched  = (sbSensor:SBSensor): boolean => {
  //On Order value is calculated from all sensors that have the Dispatched Flag as true.
  if (sbSensor?.dispatched) {
    return true;
  }
  return false;
};

export function getOnHandSensors(partNumber: string): SDASensor[] {
  if (!partNumber) return [];

  const batchedSensors = sensorBatchStore
    .getBatchSensorsByPartNumber(partNumber)
    .filter((sb) => isOnHand(sb, stockManagementStore.getSensorByMacAddress(sb.macAddress)));

  if (!batchedSensors.length) return [];

  // Map the sensors to SDASensor format
  return batchedSensors.map((s) => ({
    macAddress: s.macAddress,
    partNumber: partNumber,
    serialNumber: s.serialNumber,
    provisioningId: s?.details?.provisioningId || "",
    allocationDate: s?.details?.allocationDate || undefined,
    dispatchDate: s?.details?.dispatchDate || undefined,
    provisioningDate: s?.details?.provisioningDate || undefined,
    provisioningDescription: s?.details?.provisioningDescription || "",
    isManufactured: s.isManufactured,
    fault: stockManagementStore.getSensorByMacAddress(s.macAddress)?.fault || "",
    allocated: s.allocated,
    dispatched: s.dispatched,
    customerId:s?.details?.customerId||undefined,
    siteId:s?.details?.siteId||undefined,
  })).sort((a, b) => a?.serialNumber?.localeCompare(b?.serialNumber)); // Sort by serial number
}

export function getAllocatedSensors(partNumber: string): SDASensor[] {
  if (!partNumber) return [];

  // Retrieve all sensors for a specific part number
  const batchedSensors = sensorBatchStore.getBatchSensorsByPartNumber(partNumber);
  if (!batchedSensors.length) return [];

  // Filter sensors using isAllocated logic
  return batchedSensors
    .filter((s) => {
      const sensor = stockManagementStore.getSensorByMacAddress(s.macAddress);
      return isAllocated(s,sensor);
    })
    .map((s) => (
      {
      macAddress: s.macAddress,
      partNumber: partNumber,
      serialNumber: s.serialNumber,
      provisioningId: s?.details?.provisioningId || "",
      allocationDate: s?.details?.allocationDate || undefined,
      dispatchDate: s?.details?.dispatchDate || undefined,
      provisioningDate: s?.details?.provisioningDate || undefined,
      provisioningDescription: s?.details?.provisioningDescription || "",
      isManufactured: s.isManufactured,
      fault: stockManagementStore.getSensorByMacAddress(s.macAddress)?.fault || "",
      allocated: s.allocated,
      dispatched: s.dispatched,
      customerId:s?.details?.customerId||undefined,
      siteId:s?.details?.siteId||undefined,
    }))
    .sort((a, b) => a?.serialNumber?.localeCompare(b?.serialNumber)); // Sort by serial number
}

export function getOnOrderSensors(partNumber: string): SDASensor[] {
  if (!partNumber) return [];

  // Retrieve all sensors for a specific part number
  const batchedSensors = sensorBatchStore
    .getBatchSensorsByPartNumber(partNumber)
    .filter((sb) => isOnOrder(sb)); // Use isOnOrder to filter sensors

  if (!batchedSensors.length) return [];

  // Map the sensors to SDASensor format
  return batchedSensors.map((s) => ({
    macAddress: s.macAddress,
    partNumber: partNumber,
    serialNumber: s.serialNumber,
    provisioningId: s?.details?.provisioningId || "",
    allocationDate: s?.details?.allocationDate || undefined,
    dispatchDate: s?.details?.dispatchDate || undefined,
    provisioningDate: s?.details?.provisioningDate || undefined,
    provisioningDescription: s?.details?.provisioningDescription || "",
    isManufactured: s.isManufactured,
    allocated: s.allocated,
    dispatched: s.dispatched,
    customerId:s?.details?.customerId||undefined,
    siteId:s?.details?.siteId||undefined,
  }));
}

export function getDispatched(partNumber: string): SDASensor[] {
  if (!partNumber) return [];

  // Retrieve and filter sensors that are dispatched
  const dispatchedBatchSensors = sensorBatchStore
    .getBatchSensorsByPartNumber(partNumber)
    .filter((sb) => isDispatched(sb)); // Use isDispatched to filter sensors

  if (!dispatchedBatchSensors.length) return [];

  // Map the sensors to SDASensor format
  return dispatchedBatchSensors.map((s) => ({
    macAddress: s.macAddress,
    partNumber: partNumber,
    serialNumber: s.serialNumber,
    provisioningId: s?.details?.provisioningId || "",
    allocationDate: s?.details?.allocationDate || undefined,
    dispatchDate: s?.details?.dispatchDate || undefined,
    provisioningDate: s?.details?.provisioningDate || undefined,
    provisioningDescription: s?.details?.provisioningDescription || "",
    isManufactured: s.isManufactured,
    allocated: s.allocated,
    dispatched: s.dispatched,
    customerId:s?.details?.customerId||undefined,
    siteId:s?.details?.siteId||undefined,
  }));
}

export function CalculateAbleToCommitSensors(
  onHandLength: number,
  onOrderLength: number,
  allocatedLength: number
) {
  return onHandLength + onOrderLength - allocatedLength;
}

export function getAbleToCommitSensorsNumber(
  partNumber: string
): number {
    const {allSensorsStockManagement : sensors}=stockManagementStore;
  if (!Array.isArray(sensors) || sensors.length < 1 || !partNumber) return 0;

  // Get on-hand, on-order, and allocated sensors
  const onHandSensors = getOnHandSensors(partNumber);
  const onOrderSensors = getOnOrderSensors(partNumber);
  const allocatedSensors = getAllocatedSensors(partNumber);

  // // Combine and deduplicate onHandSensors and onOrderSensors
  //const combinedSensors = [...onHandSensors, ...onOrderSensors]
  //    .filter((sensor, index, self) =>
  //        index === self.findIndex(s => s.id === sensor.id)
  //    );

  // // Filter out allocatedSensors from combinedSensors
  //const availableSensors = combinedSensors.filter(sensor =>
  //    !allocatedSensors.some(allocatedSensor => allocatedSensor.id === sensor.id)
  //);
  /* console.log(partNumber, availableSensors);*/

  return CalculateAbleToCommitSensors(
    onHandSensors.length,
    onOrderSensors.length,
    allocatedSensors.length
  );
}
